import { useState } from "react";
import { AlertCircle, Filter, X, Trash2, Code2, Copy, Check } from "lucide-react";
import axios from "axios";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "@/components/ui/dialog";
import { ScrollArea } from "@/components/ui/scroll-area";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { toast } from "sonner";

export const PidTable = ({ data, onProcessKilled }) => {
  const [searchQuery, setSearchQuery] = useState("");
  const [stateFilter, setStateFilter] = useState("all");
  const [databaseFilter, setDatabaseFilter] = useState("all");
  const [userFilter, setUserFilter] = useState("all");
  const [killDialogOpen, setKillDialogOpen] = useState(false);
  const [selectedPid, setSelectedPid] = useState(null);
  const [killing, setKilling] = useState(false);
  const [queryDialogOpen, setQueryDialogOpen] = useState(false);
  const [selectedQuery, setSelectedQuery] = useState(null);
  const [copied, setCopied] = useState(false);

  const calculateTotalSeconds = (duration) => {
    if (!duration) return 0;
    const days = duration.days || 0;
    const hours = duration.hours || 0;
    const minutes = duration.minutes || 0;
    const seconds = duration.seconds || 0;
    return days * 86400 + hours * 3600 + minutes * 60 + seconds;
  };

  const formatDuration = (duration) => {
    if (!duration) return "0s";
    const parts = [];
    if (duration.days) parts.push(`${duration.days}d`);
    if (duration.hours) parts.push(`${duration.hours}h`);
    if (duration.minutes) parts.push(`${duration.minutes}m`);
    if (duration.seconds) parts.push(`${duration.seconds}s`);
    return parts.join(" ") || "0s";
  };

  const getRowColorClass = (duration) => {
    const totalSeconds = calculateTotalSeconds(duration);
    if (totalSeconds > 60) {
      return "bg-danger/10 hover:bg-danger/20 border-l-4 border-l-danger";
    } else if (totalSeconds > 30) {
      return "bg-warning/10 hover:bg-warning/20 border-l-4 border-l-warning";
    }
    return "table-row-hover";
  };

  const getDurationBadgeVariant = (duration) => {
    const totalSeconds = calculateTotalSeconds(duration);
    if (totalSeconds > 60) {
      return "danger";
    } else if (totalSeconds > 30) {
      return "warning";
    }
    return "secondary";
  };

  const getStateBadge = (state) => {
    const stateMap = {
      active: { variant: "success", label: "Active" },
      idle: { variant: "secondary", label: "Idle" },
      waiting: { variant: "warning", label: "Waiting" },
    };
    const stateInfo = stateMap[state?.toLowerCase()] || { variant: "secondary", label: state };
    
    return (
      <Badge
        variant="outline"
        className={`font-mono ${
          stateInfo.variant === "success"
            ? "bg-success/10 text-success border-success/30"
            : stateInfo.variant === "warning"
            ? "bg-warning/10 text-warning border-warning/30"
            : "bg-secondary"
        }`}
      >
        {stateInfo.label}
      </Badge>
    );
  };

  const formatTimestamp = (timestamp) => {
    if (!timestamp) return "--";
    const date = new Date(timestamp);
    return date.toLocaleString();
  };

  // Get unique values for filters
  const uniqueDatabases = [...new Set(data?.map(pid => pid.dbname).filter(Boolean))];
  const uniqueUsers = [...new Set(data?.map(pid => pid.user).filter(Boolean))];
  const uniqueStates = [...new Set(data?.map(pid => pid.state).filter(Boolean))];

  // Filter data
  const filteredData = data?.filter(pid => {
    const matchesSearch = !searchQuery || 
      pid.query?.toLowerCase().includes(searchQuery.toLowerCase()) ||
      pid.pid?.toString().includes(searchQuery) ||
      pid.dbname?.toLowerCase().includes(searchQuery.toLowerCase()) ||
      pid.user?.toLowerCase().includes(searchQuery.toLowerCase());
    
    const matchesState = stateFilter === "all" || pid.state?.toLowerCase() === stateFilter.toLowerCase();
    const matchesDatabase = databaseFilter === "all" || pid.dbname === databaseFilter;
    const matchesUser = userFilter === "all" || pid.user === userFilter;

    return matchesSearch && matchesState && matchesDatabase && matchesUser;
  }) || [];

  const hasActiveFilters = searchQuery || stateFilter !== "all" || databaseFilter !== "all" || userFilter !== "all";

  const clearFilters = () => {
    setSearchQuery("");
    setStateFilter("all");
    setDatabaseFilter("all");
    setUserFilter("all");
  };

  const handleKillProcess = async (pid) => {
    setKilling(true);
    try {
      const response = await axios.get(`https://coopsys.app/metrics/pg/kill/${pid}`);
      toast.success(`Process ${pid} terminated successfully`, {
        description: "The process has been killed."
      });
      setKillDialogOpen(false);
      setSelectedPid(null);
      
      // Notify parent to refresh data
      if (onProcessKilled) {
        onProcessKilled();
      }
    } catch (error) {
      console.error("Error killing process:", error);
      toast.error(`Failed to kill process ${pid}`, {
        description: error.response?.data?.message || error.message || "An error occurred"
      });
    } finally {
      setKilling(false);
    }
  };

  const confirmKill = (pid) => {
    setSelectedPid(pid);
    setKillDialogOpen(true);
  };

  const handleViewQuery = (query) => {
    setSelectedQuery(query);
    setQueryDialogOpen(true);
    setCopied(false);
  };

  const handleCopyQuery = async () => {
    if (selectedQuery) {
      try {
        await navigator.clipboard.writeText(selectedQuery);
        setCopied(true);
        setTimeout(() => setCopied(false), 2000);
      } catch (err) {
        console.error("Failed to copy:", err);
      }
    }
  };

  return (
    <>
      <Card className="border-border">
        <CardContent className="p-0">
          {/* Filters */}
          <div className="p-4 border-b border-border bg-muted/30 space-y-4">
            <div className="flex items-center justify-between">
              <div className="flex items-center space-x-2">
                <Filter className="h-4 w-4 text-muted-foreground" />
                <span className="text-sm font-medium text-foreground">Filters</span>
              </div>
              {hasActiveFilters && (
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={clearFilters}
                  className="h-8 text-xs gap-1"
                >
                  <X className="h-3 w-3" />
                  Clear Filters
                </Button>
              )}
            </div>
            
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-3">
              {/* Search */}
              <div className="space-y-1">
                <label className="text-xs text-muted-foreground">Search</label>
                <Input
                  placeholder="PID, query, user, database..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="h-9"
                />
              </div>

              {/* State Filter */}
              <div className="space-y-1">
                <label className="text-xs text-muted-foreground">State</label>
                <Select value={stateFilter} onValueChange={setStateFilter}>
                  <SelectTrigger className="h-9">
                    <SelectValue placeholder="All states" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All States</SelectItem>
                    {uniqueStates.map(state => (
                      <SelectItem key={state} value={state.toLowerCase()}>
                        {state}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              {/* Database Filter */}
              <div className="space-y-1">
                <label className="text-xs text-muted-foreground">Database</label>
                <Select value={databaseFilter} onValueChange={setDatabaseFilter}>
                  <SelectTrigger className="h-9">
                    <SelectValue placeholder="All databases" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All Databases</SelectItem>
                    {uniqueDatabases.map(db => (
                      <SelectItem key={db} value={db}>
                        {db}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              {/* User Filter */}
              <div className="space-y-1">
                <label className="text-xs text-muted-foreground">User</label>
                <Select value={userFilter} onValueChange={setUserFilter}>
                  <SelectTrigger className="h-9">
                    <SelectValue placeholder="All users" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All Users</SelectItem>
                    {uniqueUsers.map(user => (
                      <SelectItem key={user} value={user}>
                        {user}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>

            {/* Results count */}
            <div className="text-xs text-muted-foreground">
              Showing {filteredData.length} of {data?.length || 0} processes
              {hasActiveFilters && " (filtered)"}
            </div>
          </div>

          {/* Table */}
          <div className="overflow-x-auto">
            <Table>
              <TableHeader>
                <TableRow className="bg-muted/50 hover:bg-muted/50">
                  <TableHead className="font-semibold">PID</TableHead>
                  <TableHead className="font-semibold">User</TableHead>
                  <TableHead className="font-semibold">Database</TableHead>
                  <TableHead className="font-semibold">State</TableHead>
                  <TableHead className="font-semibold">Duration</TableHead>
                  <TableHead className="font-semibold">Query Start</TableHead>
                  <TableHead className="font-semibold">Query</TableHead>
                  <TableHead className="font-semibold text-center">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {filteredData && filteredData.length > 0 ? (
                  filteredData.map((pid, index) => {
                    const totalSeconds = calculateTotalSeconds(pid.duration);
                    const isWarning = totalSeconds > 30 && totalSeconds <= 60;
                    const isDanger = totalSeconds > 60;

                    return (
                      <TableRow key={index} className={getRowColorClass(pid.duration)}>
                        <TableCell className="font-medium mono-text">
                          <div className="flex items-center space-x-2">
                            {(isWarning || isDanger) && (
                              <TooltipProvider>
                                <Tooltip>
                                  <TooltipTrigger>
                                    <AlertCircle
                                      className={`h-4 w-4 ${
                                        isDanger ? "text-danger" : "text-warning"
                                      }`}
                                    />
                                  </TooltipTrigger>
                                  <TooltipContent>
                                    <p>
                                      {isDanger
                                        ? "Long running query (>1 min)"
                                        : "Query running for >30 seconds"}
                                    </p>
                                  </TooltipContent>
                                </Tooltip>
                              </TooltipProvider>
                            )}
                            <span>{ pid.pid  }</span>
                          </div>
                        </TableCell>
                        <TableCell className="text-sm">{pid.user}</TableCell>
                        <TableCell className="text-sm mono-text">{pid.dbname}</TableCell>
                        <TableCell>{getStateBadge(pid.state)}</TableCell>
                        <TableCell>
                          <Badge
                            variant="outline"
                            className={`font-mono ${
                              isDanger
                                ? "bg-danger/10 text-danger border-danger/30"
                                : isWarning
                                ? "bg-warning/10 text-warning border-warning/30"
                                : "bg-secondary"
                            }`}
                          >
                            {formatDuration(pid.duration)}
                          </Badge>
                        </TableCell>
                        <TableCell className="text-sm text-muted-foreground mono-text">
                          {formatTimestamp(pid.query_start)}
                        </TableCell>
                        <TableCell className="max-w-md">
                          <div className="flex items-center gap-2">
                            <TooltipProvider>
                              <Tooltip>
                                <TooltipTrigger asChild>
                                  <p className="text-sm text-muted-foreground truncate mono-text cursor-help flex-1">
                                    {pid.query}
                                  </p>
                                </TooltipTrigger>
                                <TooltipContent className="max-w-lg">
                                  <p className="text-xs mono-text whitespace-pre-wrap">{pid.query}</p>
                                </TooltipContent>
                              </Tooltip>
                            </TooltipProvider>
                            <TooltipProvider>
                              <Tooltip>
                                <TooltipTrigger asChild>
                                  <Button
                                    variant="ghost"
                                    size="sm"
                                    onClick={() => handleViewQuery(pid.query)}
                                    className="h-7 w-7 p-0 flex-shrink-0"
                                  >
                                    <Code2 className="h-3.5 w-3.5" />
                                  </Button>
                                </TooltipTrigger>
                                <TooltipContent>
                                  <p>View full query</p>
                                </TooltipContent>
                              </Tooltip>
                            </TooltipProvider>
                          </div>
                        </TableCell>
                       
                        <TableCell className="text-center">
                          <TooltipProvider>
                            <Tooltip>
                              <TooltipTrigger asChild>
                                <Button
                                  variant="ghost"
                                  size="sm"
                                  onClick={() => confirmKill(pid.pid)}
                                  className="h-8 w-8 p-0 text-destructive hover:text-destructive hover:bg-destructive/10"
                                >
                                  <Trash2 className="h-4 w-4" />
                                </Button>
                              </TooltipTrigger>
                              <TooltipContent>
                                <p>Kill process</p>
                              </TooltipContent>
                            </Tooltip>
                          </TooltipProvider>
                        </TableCell>
                      </TableRow>
                    );
                  })
                ) : (
                  <TableRow>
                    <TableCell colSpan={9} className="text-center text-muted-foreground py-8">
                      {hasActiveFilters 
                        ? "No processes match the current filters"
                        : "No process information available"}
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </div>
        </CardContent>
      </Card>

      {/* Kill Confirmation Dialog */}
      <AlertDialog open={killDialogOpen} onOpenChange={setKillDialogOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Kill Process?</AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to terminate the process with PID <span className="font-bold mono-text">{selectedPid}</span>?
              This action cannot be undone and will immediately terminate the running query.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={killing}>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={() => handleKillProcess(selectedPid)}
              disabled={killing}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
            >
              {killing ? "Killing..." : "Kill Process"}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Query View Dialog */}
      <Dialog open={queryDialogOpen} onOpenChange={setQueryDialogOpen}>
        <DialogContent className="max-w-4xl max-h-[80vh]">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Code2 className="h-5 w-5 text-primary" />
              Query Details
            </DialogTitle>
            <DialogDescription>
              Full SQL query - Click copy button to copy to clipboard
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4">
            {/* Query Display */}
            <div className="relative">
              <ScrollArea className="h-96 rounded-md border border-border bg-muted/30 p-4">
                <pre className="text-sm mono-text text-foreground whitespace-pre-wrap break-words">
                  {selectedQuery}
                </pre>
              </ScrollArea>
              
              {/* Copy Button */}
              <Button
                onClick={handleCopyQuery}
                size="sm"
                variant="outline"
                className="absolute top-2 right-2 gap-2"
              >
                {copied ? (
                  <>
                    <Check className="h-4 w-4 text-success" />
                    Copied!
                  </>
                ) : (
                  <>
                    <Copy className="h-4 w-4" />
                    Copy Query
                  </>
                )}
              </Button>
            </div>

            {/* Query Stats */}
            <div className="grid grid-cols-2 gap-4 p-3 bg-muted/30 rounded-lg border border-border">
              <div>
                <p className="text-xs text-muted-foreground">Query Length</p>
                <p className="text-sm font-medium text-foreground mono-text">
                  {selectedQuery?.length || 0} characters
                </p>
              </div>
              <div>
                <p className="text-xs text-muted-foreground">Lines</p>
                <p className="text-sm font-medium text-foreground mono-text">
                  {selectedQuery?.split('\n').length || 0} lines
                </p>
              </div>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
};

export default PidTable;
